import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import { listVoices, isVoiceCompatibleWith, type ElevenVoice } from '../pipeline/elevenlabs';
import { isSyncConfigured } from '../lib/syncClient';
import { backgroundExtractCanon } from '../pipeline/stages';
import { prose as proseStore } from '../storage/db';
import { DEFAULTS, TEMPLATE_DESCRIPTIONS, TEMPLATE_ORDER } from '../templates/registry';
import type { PromptTemplateId } from '../types';
import './routes.css';

export default function UniverseSettingsRoute() {
  const activeUniverseId = useAppStore((s) => s.ui.activeUniverseId);
  const universes = useAppStore((s) => s.universes);
  const settings = useAppStore((s) => s.settings);
  const stories = useAppStore((s) => s.stories);
  const updateUniverse = useAppStore((s) => s.updateUniverse);
  const archiveUniverse = useAppStore((s) => s.archiveUniverse);
  const setUniverseOverride = useAppStore((s) => s.setUniverseOverride);
  const clearUniverseOverride = useAppStore((s) => s.clearUniverseOverride);
  const setUniverseExtension = useAppStore((s) => s.setUniverseExtension);
  const clearUniverseExtension = useAppStore((s) => s.clearUniverseExtension);
  const nav = useNavigate();

  const universe = activeUniverseId ? universes[activeUniverseId] : undefined;

  const [voices, setVoices] = useState<ElevenVoice[]>([]);
  const [voicesErr, setVoicesErr] = useState<string | undefined>();
  const [editing, setEditing] = useState<PromptTemplateId | undefined>();
  const [draft, setDraft] = useState('');
  const [extDraft, setExtDraft] = useState('');
  const [reextractError, setReextractError] = useState<string | undefined>();

  const latestProseStory = useMemo(() => {
    if (!activeUniverseId) return undefined;
    return Object.values(stories)
      .filter((s) => s.universeId === activeUniverseId && s.proseKey)
      .sort((a, b) => b.updatedAt - a.updatedAt)[0];
  }, [stories, activeUniverseId]);

  const canGenerate = isSyncConfigured();

  useEffect(() => {
    setVoicesErr(undefined);
    if (!canGenerate) {
      setVoices([]);
      return;
    }
    let cancelled = false;
    listVoices()
      .then((vs) => {
        if (!cancelled) setVoices(vs);
      })
      .catch((e) => {
        if (!cancelled) setVoicesErr((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [canGenerate]);

  if (!universe) {
    return (
      <div className="route">
        <h1>Universe settings</h1>
        <div className="dim">No active universe. Pick one from the home screen.</div>
        <button onClick={() => nav('/')}>Go to universes</button>
      </div>
    );
  }

  function openEditor(id: PromptTemplateId) {
    if (!universe) return;
    setEditing(id);
    setDraft(universe.overrides[id] ?? DEFAULTS[id]);
    setExtDraft(universe.extensions[id] ?? '');
  }

  function saveEditor() {
    if (!universe || !editing) return;
    if (draft === DEFAULTS[editing]) {
      clearUniverseOverride(universe.id, editing);
    } else {
      setUniverseOverride(universe.id, editing, draft);
    }
    if (extDraft.trim()) {
      setUniverseExtension(universe.id, editing, extDraft);
    } else {
      clearUniverseExtension(universe.id, editing);
    }
    setEditing(undefined);
  }

  function revertMain() {
    if (!editing) return;
    setDraft(DEFAULTS[editing]);
  }

  function onArchive() {
    if (!universe) return;
    if (!confirm(`Archive "${universe.name}"? You can restore it from Archive.`)) return;
    archiveUniverse(universe.id);
    nav('/');
  }

  async function onReextractFromLatest() {
    if (!universe || !latestProseStory) return;
    setReextractError(undefined);
    try {
      const text = await proseStore.get(latestProseStory.id);
      if (!text) {
        setReextractError("Couldn't load the latest story's prose.");
        return;
      }
      void backgroundExtractCanon(universe.id, settings, text, latestProseStory.id);
    } catch (e) {
      setReextractError((e as Error).message);
    }
  }

  return (
    <div className="route">
      <h1>{universe.name}</h1>

      <section className="card">
        <h2>Identity</h2>
        <label>
          <div className="label">Name</div>
          <input
            value={universe.name}
            onChange={(e) => updateUniverse(universe.id, { name: e.target.value })}
          />
        </label>
        <label>
          <div className="label">Description (optional)</div>
          <input
            value={universe.description ?? ''}
            onChange={(e) => updateUniverse(universe.id, { description: e.target.value })}
            placeholder="A short blurb shown on the home screen"
          />
        </label>
        <label>
          <div className="label">Default protagonists</div>
          <input
            value={universe.defaultProtagonists}
            onChange={(e) =>
              updateUniverse(universe.id, { defaultProtagonists: e.target.value })
            }
            placeholder="e.g., the protagonists by name and role"
          />
        </label>
      </section>

      <section className="card">
        <h2>Default voice</h2>
        <div className="dim small">
          Used for TTS unless a story overrides it at the audio step.
        </div>
        {voicesErr && <div className="warn small">{voicesErr}</div>}
        {voices.length > 0 ? (
          <>
            <div className="voice-picker-row">
              <select
                value={universe.defaultVoiceId ?? ''}
                onChange={(e) =>
                  updateUniverse(universe.id, { defaultVoiceId: e.target.value || undefined })
                }
              >
                <option value="">— pick a voice —</option>
                {[...voices]
                  .sort((a, b) => {
                    const aV3 = isVoiceCompatibleWith(a, settings.ttsModel);
                    const bV3 = isVoiceCompatibleWith(b, settings.ttsModel);
                    if (aV3 !== bV3) return aV3 ? -1 : 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((v) => {
                    const compat = isVoiceCompatibleWith(v, settings.ttsModel);
                    return (
                      <option key={v.voice_id} value={v.voice_id}>
                        {compat ? '✓ ' : '· '}
                        {v.name}
                        {!compat ? ` — not tuned for ${settings.ttsModel}` : ''}
                      </option>
                    );
                  })}
              </select>
              <VoicePreviewButton
                voice={voices.find((v) => v.voice_id === universe.defaultVoiceId)}
              />
            </div>
            <VoiceCompatNote
              voices={voices}
              selectedId={universe.defaultVoiceId}
              modelId={settings.ttsModel}
            />
          </>
        ) : (
          <input
            value={universe.defaultVoiceId ?? ''}
            onChange={(e) =>
              updateUniverse(universe.id, { defaultVoiceId: e.target.value || undefined })
            }
            placeholder="voice id"
          />
        )}
      </section>

      <section className="card">
        <h2>
          Canon{' '}
          {universe.canonInFlight && (
            <span className="tag tag-soft" style={{ marginLeft: 8 }}>updating…</span>
          )}
        </h2>
        <div className="dim small">
          Auto-extracted facts from completed stories — names, places, recurring objects,
          small world-rules. Updated in the background after every prose generation or
          revision. Edit freely; this text is injected as system context for future stories.
        </div>
        <textarea
          rows={14}
          value={universe.canon ?? ''}
          onChange={(e) => updateUniverse(universe.id, { canon: e.target.value })}
          placeholder="(empty — will populate as stories are written)"
        />
        {reextractError && <div className="warn small">{reextractError}</div>}
        <div className="row">
          <button
            onClick={onReextractFromLatest}
            disabled={universe.canonInFlight || !latestProseStory}
          >
            {universe.canonInFlight
              ? 'Updating…'
              : latestProseStory
                ? `Re-extract from "${latestProseStory.slug.title}"`
                : 'No stories with prose yet'}
          </button>
          {universe.canonUpdatedAt && (
            <span className="dim small">
              Last updated {new Date(universe.canonUpdatedAt).toLocaleString()}
            </span>
          )}
        </div>
      </section>

      <section className="card">
        <h2>Prompts</h2>
        <div className="dim small">
          Override fully replaces the shipped default. Extension is appended underneath
          and stays even when the default updates.
        </div>
        {TEMPLATE_ORDER.map((id) => {
          const overridden = universe.overrides[id] != null;
          const extended = !!universe.extensions[id]?.trim();
          return (
            <div key={id} className="prompt-row">
              <div>
                <div className="slug-title">{id}</div>
                <div className="dim small">{TEMPLATE_DESCRIPTIONS[id]}</div>
              </div>
              <div className="prompt-row-actions">
                {!overridden && !extended && <span className="dim small">default</span>}
                {overridden && <span className="tag">overridden</span>}
                {extended && <span className="tag tag-soft">+ extension</span>}
                <button onClick={() => openEditor(id)}>Edit</button>
                {overridden && (
                  <button
                    className="ghost"
                    onClick={() => clearUniverseOverride(universe.id, id)}
                  >
                    Revert
                  </button>
                )}
                {extended && (
                  <button
                    className="ghost"
                    onClick={() => clearUniverseExtension(universe.id, id)}
                  >
                    Clear ext
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </section>

      <section className="card">
        <h2>Archive universe</h2>
        <div className="dim small">
          Hides this universe from the home screen. Stories, slugs, and settings stay intact.
        </div>
        <button className="ghost" onClick={onArchive}>
          Archive "{universe.name}"
        </button>
      </section>

      {editing && (
        <div className="editor-modal">
          <div className="editor-panel">
            <div className="row">
              <h2>{editing}</h2>
              <div className="spacer" />
              <button className="ghost" onClick={() => setEditing(undefined)}>
                Cancel
              </button>
              <button className="primary" onClick={saveEditor}>
                Save
              </button>
            </div>

            <div className="editor-section">
              <div className="row">
                <div className="label" style={{ flex: 1 }}>
                  Main template
                  {draft !== DEFAULTS[editing] ? (
                    <span className="tag" style={{ marginLeft: 8 }}>differs from default</span>
                  ) : (
                    <span className="dim small" style={{ marginLeft: 8 }}>matches default</span>
                  )}
                </div>
                <button
                  className="ghost"
                  onClick={revertMain}
                  disabled={draft === DEFAULTS[editing]}
                >
                  Revert to default
                </button>
              </div>
              <textarea
                rows={16}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
            </div>

            <div className="editor-section">
              <div className="row">
                <div className="label" style={{ flex: 1 }}>
                  Extension (appended after main with a blank line)
                </div>
                <button className="ghost" onClick={() => setExtDraft('')} disabled={!extDraft}>
                  Clear
                </button>
              </div>
              <div className="dim small">
                Use this for local tweaks you want to keep across app updates — the main
                template stays at default and your extension is added below.
              </div>
              <textarea
                rows={6}
                value={extDraft}
                placeholder="(empty — nothing appended)"
                onChange={(e) => setExtDraft(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VoicePreviewButton({ voice }: { voice?: ElevenVoice }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const previewUrl = voice?.preview_url;

  // Stop playback whenever the selected voice changes.
  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    setPlaying(false);
    setError(undefined);
  }, [previewUrl]);

  if (!voice) {
    return (
      <button type="button" className="ghost" disabled title="Pick a voice first">
        ▶ Preview
      </button>
    );
  }
  if (!previewUrl) {
    return (
      <button
        type="button"
        className="ghost"
        disabled
        title="No preview available for this voice"
      >
        ▶ Preview
      </button>
    );
  }

  function toggle() {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      el.currentTime = 0;
      setPlaying(false);
      return;
    }
    setError(undefined);
    el.play()
      .then(() => setPlaying(true))
      .catch((e) => setError((e as Error).message));
  }

  return (
    <>
      <button type="button" className="ghost" onClick={toggle}>
        {playing ? '■ Stop' : '▶ Preview'}
      </button>
      <audio
        ref={audioRef}
        src={previewUrl}
        preload="none"
        onEnded={() => setPlaying(false)}
        onError={() => {
          setPlaying(false);
          setError('Preview failed to load.');
        }}
      />
      {error && <div className="warn small">{error}</div>}
    </>
  );
}

function VoiceCompatNote({
  voices,
  selectedId,
  modelId,
}: {
  voices: ElevenVoice[];
  selectedId?: string;
  modelId: string;
}) {
  if (!selectedId) return null;
  const voice = voices.find((v) => v.voice_id === selectedId);
  if (!voice) return null;
  const compat = isVoiceCompatibleWith(voice, modelId);
  if (compat) return null;
  return (
    <div className="warn small">
      "{voice.name}" isn't tuned for <code>{modelId}</code> — performance tags
      like <code>[soft]</code> or <code>[whispers]</code> will be read aloud as
      words rather than performed. Pick a ✓ voice, or change the TTS model in
      Settings.
    </div>
  );
}
